import { API_ENDPOINTS, getApiUrl } from "../config";
import { buildBffRequestHeaders } from "./bffRequestHeaders";
import { searchLocationSuggestions } from "./location.service";

export const loadCountries = async () => {
    const url = getApiUrl(API_ENDPOINTS.LOAD_COUNTRIES);
    try {
        const response = await fetch(url);
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const text = await response.text();
        
        let data;
        try {
            data = JSON.parse(text);
        } catch (parseError) {
            console.error('Response was not valid JSON');
            throw new Error('API returned invalid JSON');
        }
        
        if (!data.success) {
            throw new Error(data.error || 'Failed to load countries');
        }
        return data;
    } catch (error) {
        console.error('Error loading countries:', error);
        throw error;
    }
}

export const getLocationDetailsByKID = async (kid: string) => {
    const url = getApiUrl(`${API_ENDPOINTS.GET_LOCATION_BY_KID}/${kid}`);
    
    try {
        const response = await fetch(url, {
            headers: buildBffRequestHeaders(),
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const text = await response.text();
        let data;
        try {
            data = JSON.parse(text);
        } catch (parseError) {
            console.error('Failed to parse JSON:', parseError);
            throw new Error('API returned invalid JSON');
        }
        
        if (!data.success) {
            throw new Error(data.error || 'Failed to load location details');
        }
        return data;
    } catch (error) {
        console.error('Error loading location details:', error);
        throw error;
    }
}

export const getPartnerHierarchy = async (agentId: string) => {
    const url = getApiUrl(`/api/common/partnerhierarchy/${encodeURIComponent(agentId)}`);
    try {
        const response = await fetch(url, {
            headers: buildBffRequestHeaders(),
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const text = await response.text();
        let data: any;
        try {
            data = JSON.parse(text);
        } catch (err) {
            console.error('Failed to parse partner hierarchy JSON:', err);
            throw new Error('API returned invalid JSON for partner hierarchy');
        }
        if (!data.success) {
            throw new Error(data.error || 'Failed to load partner hierarchy');
        }
        // If backend returned raw string JSON in data
        let payload = data;
        if (typeof data.data === 'string') {
            try {
                payload = JSON.parse(data.data);
            } catch (err) {
                console.warn('Partner hierarchy data field is not JSON string; using wrapper');
            }
        } else if (typeof data.data === 'object') {
            payload = data.data;
        }
        return payload;
    } catch (error) {
        console.error('Error loading partner hierarchy:', error);
        throw error;
    }
}

export const postFeeLookup = async (feeLookupData: any) => {
    const url = getApiUrl(API_ENDPOINTS.FEE_LOOKUP_INIT);
    
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: buildBffRequestHeaders(),
            body: JSON.stringify(feeLookupData)
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const text = await response.text();
        let data;
        try {
            data = JSON.parse(text);
            // Parse the nested data property if it's a JSON string
            let feeData = data;
            if (data.data && typeof data.data === 'string') {
                try {
                    feeData = JSON.parse(data.data);
                } catch (nestedParseError) {
                    throw new Error('API returned invalid nested JSON');
                }
            }
        } catch (parseError) {
            console.error('Failed to parse JSON:', parseError);
            throw new Error('API returned invalid JSON');
        }
        
        if (!data.success) {
            throw new Error(data.error || 'Failed to perform fee lookup');
        }

        // Process fee infos to get unique currencies and exchange rates
        let feeData = data;
        if (data.data && typeof data.data === 'string') {
            try {
                feeData = JSON.parse(data.data);
            } catch (nestedParseError) {
                console.error('Failed to parse nested data JSON:', nestedParseError);
                throw new Error('API returned invalid nested JSON');
            }
        }

        if (feeData.FeeInfos && Array.isArray(feeData.FeeInfos)) {
            // Helper function to get receive currency (same logic as getReceiveCurrency method)
            const getReceiveCurrency = (fee: any): string => {
                if (fee.ReceiveAmounts?.ValidCurrencyIndicator) {
                    return fee.ValidReceiveCurrency || '';
                } else {
                    return fee.EstimatedReceiveCurrency || (fee.ValidReceiveCurrency || '');
                }
            };
            
            // Group by receive currency and get first fee info for each currency
            const uniqueFeeInfos = feeData.FeeInfos.reduce((acc: any[], fee: any) => {
                const receiveCurrency = getReceiveCurrency(fee);
                if (!acc.find((f: any) => getReceiveCurrency(f) === receiveCurrency)) {
                    acc.push(fee);
                }
                return acc;
            }, []);

            // Add exchange rate information. Pick NoN USD
            const exchangeRates = uniqueFeeInfos.map((fee: any) => ({
                receiveCurrency: getReceiveCurrency(fee),
                exchangeRate: fee.ValidExchangeRate || fee.EstimatedExchangeRate,
                isEstimated: !fee.ValidExchangeRate && fee.EstimatedExchangeRate,
                totalSendFees: Number(fee?.SendAmounts?.TotalSendFees ?? 0),
                totalAmountToCollect: Number(
                    fee?.SendAmounts?.TotalAmountToCollect ?? fee?.TotalAmount ?? 0,
                ),
            })).filter((fee: any) => fee.receiveCurrency !== 'USD'); 

            data.exchangeRates = exchangeRates;
            console.log("Response fee lookup data exchangeRates:", data.exchangeRates);
        } else {
            console.log("No FeeInfos found or not an array");
        }
        return data;
    } catch (error) {
        console.error('Error performing fee lookup:', error);
        throw error;
    }
}

/** Normalized row for address autocomplete lists (AWS Location suggestion shape). */
export type AddressSuggestion = {
  placeId: string;
  description: string;
  mainText?: string;
  secondaryText?: string;
};

/**
 * Address suggestions (GoogleAddressInputStep; name kept for compatibility).
 * Uses the same AWS Location suggestions endpoint as other address steps.
 */
export async function getGoogleAddressAutocomplete(
  query: string
): Promise<AddressSuggestion[]> {
  const q = query.trim();
  if (q.length < 2) {
    return [];
  }
  const raw = await searchLocationSuggestions(q);
  return (raw || []).map((item: any) => {
    const text = String(item?.Text ?? "");
    return {
      placeId: String(item?.PlaceId ?? ""),
      description: text,
      mainText: text,
      secondaryText: item?.Category != null ? String(item.Category) : undefined,
    };
  });
}
